import { Injectable } from '@angular/core';
import { User } from '@mitel/cloudlink-sdk';
import { MiVBInfoEmailBody, MiVBPasswordEmailBody } from '../interfaces/mivb-emails-interfaces';
import * as _ from 'lodash';
import { TranslateService } from '@ngx-translate/core';
import { NotifierService } from 'angular-notifier';
import { AdminService } from './admin.service';
import { AccountsService } from './accounts.service';
import { EmployeeService, MiccbApiService } from '@mitel-internal/miccb-components';
import { Employee } from '@mitel-internal/miccb-components/lib/interfaces/employee';
import { UserProdMiCCBService } from './user-prod-miccb.service';
import { MiCCInfoEmailBody, MiCCPasswordEmailBody } from '../interfaces/micc-email-interface';

@Injectable({
  providedIn: 'root'
})

export class MiCCEmailService {

  constructor(
    private accountService: AccountsService,
    private adminService: AdminService,
    private translateService: TranslateService,
    private notifierService: NotifierService,
    private employeeService: EmployeeService,
    private miccApiService: MiccbApiService,
    private miccbUserProdService: UserProdMiCCBService
  ) { }

  // Used for sending deploy emails for accounts without MiVB.  In this case, only CC data is contained in the email.
  public async deployMiCCUser(employee: any): Promise<any> {
    let applicationName = 'MiContact Center';
    let defaultLanguageCode = 'en';

    this.notifierService.notify('info', `${applicationName} email operation in progress`);

    try {

      if (!employee || !employee.cloudlinkId) {
        throw 'missing cloudlink userId';
      }

      const cloudLinkUser = await this.adminService.getUserWithoutFrills(employee.cloudlinkId);
      const account = this.accountService.getCurrentAccount();

      if (!account || !account.accountId || account.accountId !== cloudLinkUser.accountId) {
        throw 'missing user\'s account details';
      }

      const languageCode = account.languageCode || defaultLanguageCode;

      let infoEmailResult: MiCCInfoEmailBody = {
        to: [cloudLinkUser.email],
        languageCode: languageCode,
        templateProperties: {
          userId: cloudLinkUser.userId,
        }
      };

      let passwordEmailResult: MiCCPasswordEmailBody = {
        to: [cloudLinkUser.email],
        languageCode: languageCode,
        templateProperties: {
          userId: cloudLinkUser.userId,
        }
      };

      const result = await this.insertMiCCDetails(cloudLinkUser, undefined, languageCode, infoEmailResult as MiVBInfoEmailBody, passwordEmailResult, employee);
      await this.adminService.sendEmailByTemplateId("mivbInfo", cloudLinkUser, result.infoEmail);
      console.log("Success sending " + applicationName + " email for user ", cloudLinkUser.userId);

      await this.adminService.sendEmailByTemplateId("mivbCredentials", cloudLinkUser, result.pwEmail);
      console.log("Success sending " + applicationName + " credentials email for user ", cloudLinkUser.userId);

      this.notifierService.notify('success', `${applicationName} email operation complete`);

      return Promise.resolve();

    } catch (error) {
      if (employee?.cloudlinkId && employee.cloudlinkId.length > 0) {
        console.error("Error sending " + applicationName + " email for user ", employee.cloudlinkId, "-->", error);
      }
      else {
        console.error("Error sending " + applicationName + " email for user -->" + error);
      }

      this.notifierService.notify('error', `${applicationName} email operation failed`);

      if (error) {
        return Promise.reject(error);
      }
      else {
        return Promise.reject(`Error sending ${applicationName} email`);
      }
    }
  }

  // Inserts MiCC information into Email template for welcome and credentials emails if available for user
  // If MiCC is not enabled, or the data cannot be obtained, the original email body is returned unchanged since the deployment email should still be sent with the available MiVB data.
  public async insertMiCCDetails(cloudLinkUser: User, translatedMiCCEmailStrings: any, languageCode: any, infoEmailBody: MiVBInfoEmailBody, passwordEmailBody: MiVBPasswordEmailBody, miccEmployee: any): Promise<{ infoEmail: MiVBInfoEmailBody, pwEmail: MiVBPasswordEmailBody }> {
    try {
      const miccbEnabled = await this.miccbUserProdService.isMiCCBEnabled(cloudLinkUser?.accountId);
      if (miccbEnabled === true) {

        if (!miccEmployee) {
          await this.employeeService.getEmployeeByCloudlinkIdOrEmail(cloudLinkUser.accountId!, cloudLinkUser.userId, cloudLinkUser.email);
          miccEmployee = this.employeeService.employee;
        }

        if (!!miccEmployee) {

          if (!translatedMiCCEmailStrings) {
            let currentLanguageCode = this.translateService.currentLang;
            translatedMiCCEmailStrings = this.getTranslatedEmailStrings(currentLanguageCode, languageCode, 'micc_emails');
          }

          let authType: string = '';
          let url: string = '';
          let authInfo: string = '';

          const enterpriseData = await this.miccApiService.getEnterprise(cloudLinkUser.accountId!).toPromise();
          if (enterpriseData) {
            if (enterpriseData.authenticationType === 'WindowsAuthentication') {
              authType = translatedMiCCEmailStrings['windows_auth'];
              authInfo = translatedMiCCEmailStrings['use_windows_auth'];
            } else if (enterpriseData.authenticationType === 'BasicAuthentication') {
              authType = translatedMiCCEmailStrings['basic_auth'];
            } else if (enterpriseData.authenticationType === 'CloudLinkAuthentication') {
              authType = translatedMiCCEmailStrings['cloudlink_auth'];
              authInfo = translatedMiCCEmailStrings['use_cloudlink_auth'];
            }

            url = enterpriseData.enterpriseUrl + '/Ignite';
          }

          infoEmailBody.templateProperties['miContactCenter'] = {
            reportingNumber: miccEmployee.reporting,
            mediaChannelEnabled: this.formatMiCCMediaTypes(miccEmployee, translatedMiCCEmailStrings),
            supervisorLicenseType: miccEmployee.supervisorLicenseType,
            authenticationType: authType,
            igniteURL: url,
            username: miccEmployee.username
          };

          if (authInfo.length === 0) {
            // Generate a new password and update the user in MiCC
            authInfo = this.employeeService.createPassword();
            await this.employeeService.updateEmployee(cloudLinkUser.accountId, miccEmployee.id, [{ password: authInfo }]);
          }

          passwordEmailBody.templateProperties['miContactCenter'] = {
            password: authInfo
          };

        } else {
          throw ('Failed to retrieve MICCB employee');
        }
      }
    } catch (err) {
      const error = `Error creating email details for MiCC user ${cloudLinkUser.userId}: `;
      console.error(error, err);
      return Promise.reject(error)
    }
    return Promise.resolve({ infoEmail: infoEmailBody, pwEmail: passwordEmailBody });
  }

  //need to pre-translate strings in the language of the account (not the app setting)
  public getTranslatedEmailStrings(currentlLanguageCode: string, desiredLanguageCode: string, name: string): any {
    this.translateService.use(desiredLanguageCode);
    let translatedEmailStrings = this.translateService.getParsedResult(this.translateService.translations[desiredLanguageCode], name);
    this.translateService.use(currentlLanguageCode);
    return translatedEmailStrings;
  };

  formatMiCCMediaTypes(employee: Employee, translatedMiCCEmailStrings: any): string {
    let formattedString = '';
    if (!!employee) {
      if (employee.isEnabledForVoice) {
        formattedString += translatedMiCCEmailStrings['voice'];
      }

      if (employee.isEnabledForEmail) {
        if (formattedString.length > 0) {
          formattedString += ' | ';
        }
        formattedString += translatedMiCCEmailStrings['email'];
      }

      if (employee.isEnabledForSMS) {
        if (formattedString.length > 0) {
          formattedString += ' | ';
        }
        formattedString += translatedMiCCEmailStrings['sms'];
      }

      if (employee.isEnabledForChat) {
        if (formattedString.length > 0) {
          formattedString += ' | ';
        }
        formattedString += translatedMiCCEmailStrings['chat'];
      }

      if (employee.isEnabledForOpenMedia) {
        if (formattedString.length > 0) {
          formattedString += ' | ';
        }
        formattedString += translatedMiCCEmailStrings['open'];
      }
    }

    return formattedString;
  }

};